import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Button, TextField, Grid } from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import clsx from "clsx";
import Title from "../../Title";
import ColoredLine from "../../ColoredLine";
import MuiPhoneNumber from "material-ui-phone-number";
import { useSelector } from "react-redux";
import ValetParkingService from "../../../services/ValetParkingService";
import apiClient from "../../../auth/apiClient";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";

export const ReceiptType = {
  EMAIL: 1,
  SMS: 2,
};

const validationSchemas = {
  [ReceiptType.EMAIL]: yup.object().shape({
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Please enter a valid email address"),
  }),
  [ReceiptType.SMS]: yup.object().shape({
    phone: yup
      .string()
      .matches(/^\+1\s?\([0-9]{3}\)\s?[0-9]{3}-[0-9]{4}$/, "Please enter a valid phone number")
      .required("Please enter a valid phone number"),
  }),
};

const CashieredDeviceSummaryReceipt = ({ receiptType, onCancel, onReceiptSent, classes }) => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const notificationStyle = useSelector((state) => {
    return {
      small: state.shiftSession?.smallScreen,
      toastLocation: state.shiftSession?.toastLocation,
    };
  });

  const entityID = useSelector((state) => state.shiftSession?.entityID);

  const ticketNumber = useSelector((state) => state.shiftSession?.transaction?.credential?.ticketID);

  const schema = validationSchemas[receiptType];

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: receiptType === ReceiptType.EMAIL ? { email: "" } : { phone: "" },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    const fetchArrivalDetails = async () => {
      const valetParkingService = new ValetParkingService(apiClient);
      try {
        const result = await valetParkingService.getArrivalDetails(entityID, ticketNumber);
        if (result?.data?.arrival) {
          const arrivalInfo = result.data.arrival;
          const updatedValues = receiptType === ReceiptType.EMAIL
            ? { email: arrivalInfo.email || "" }
            : { phone: arrivalInfo.phoneNumber || "" };
          reset(updatedValues);
        }
      } catch (error) {
        enqueueSnackbar("Failed to fetch arrival details", {
          variant: "error",
          tag: "failedToFetchArrivalDetails",
          anchorOrigin: notificationStyle.toastLocation,
          fullwidth: notificationStyle.small,
        });
      }
    };

    fetchArrivalDetails();
  }, [entityID, ticketNumber, receiptType, reset]);

  const getHeaderText = () => {
    return receiptType === ReceiptType.EMAIL
      ? "Please Enter Email Address"
      : "Please Enter Phone Number";
  };

  const getFormContent = () => {
    if (receiptType === ReceiptType.EMAIL) {
      return (
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="email"
              label="Email Address *"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message || " "}
              autoFocus
            />
          )}
        />
      );
    } else {
      return (
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <MuiPhoneNumber
              {...field}
              id="phone"
              label="Phone Number *"
              variant="outlined"
              fullWidth
              defaultCountry="us"
              onlyCountries={["us"]}
              countryCodeEditable={false}
              error={!!errors.phone}
              helperText={errors.phone?.message || " "}
              autoFocus
            />
          )}
        />
      );
    }
  };

  const handleFormSubmit = async (formData) => {
    // below console statement is temporary and should be replaced with actual API call to send receipt.
    console.log(
      `${receiptType === ReceiptType.EMAIL ? "Email" : "Phone"} to send receipt:`,
      JSON.stringify(formData)
    );
    onReceiptSent();
  };

  return (
    <div>
      <Title className={clsx(`${receiptType}-receipt-title`)}>
        {getHeaderText()}
      </Title>
      <ColoredLine className={classes.coloredLineSpacing} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {getFormContent()}
        </Grid>
        <Grid item xs={12} className={classes.buttonContainer}>
          <Button
            color="primary"
            name="submit"
            variant="contained"
            onClick={handleSubmit(handleFormSubmit)}
            data-testid="sendReceiptBtn"
            className="send-receipt-btn"
          >
            Send Receipt
          </Button>
          <Button
            variant="contained"
            onClick={onCancel}
            data-testid="cancelBtn"
            className="cancel-receipt-btn"
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

CashieredDeviceSummaryReceipt.defaultProps = {
  onCancel: () => {},
  onReceiptSent: () => {},
  classes: {},
};

CashieredDeviceSummaryReceipt.propTypes = {
  receiptType: PropTypes.oneOf([ReceiptType.EMAIL, ReceiptType.SMS]).isRequired,
  onCancel: PropTypes.func,
  onReceiptSent: PropTypes.func,
  classes: PropTypes.object,
};

export default CashieredDeviceSummaryReceipt;
